import { useEffect, useRef, useState } from 'react';
import { useRevalidator } from 'react-router';

import { Icon } from '@ds/core';
import { Select } from '@ds/forms';
import { Menu, useTopbarCompacta } from '@ds/navigation';
import { dados } from '@/data/porta';
import type { UnidadeDaClinica } from '@/data/tipos';
import { atual, trocarUnidade } from '@/session/sessao';
import { EVENTO_DAS_UNIDADES, unidadeAtiva } from '@/session/unidadeAtiva';

/* O Select já usa o valor vazio para o marcador "Selecione". */
const TODAS = 'todas';

/**
 * O seletor de unidade da topbar, ao lado da clínica — trilha T7.
 *
 * "Todas as unidades" é o padrão. Escolher uma recarrega a tela no recorte dela
 * (agenda, atendimentos, contas). Com uma unidade só não há o que escolher, e o
 * seletor não aparece. Se a unidade guardada foi arquivada, a escolha volta
 * para "Todas" em vez de deixar a tela vazia sem motivo à vista.
 */
export function SeletorDeUnidade() {
    const sessao = atual();
    const clinicaId = sessao?.perfil === 'clinica' && !sessao.impersonando ? sessao.clinicaId : null;
    const revalidator = useRevalidator();
    /* O revalidador muda a cada navegação; reler a lista por isso seria à toa. */
    const revalidar = useRef(revalidator.revalidate);
    revalidar.current = revalidator.revalidate;
    const [unidades, setUnidades] = useState<UnidadeDaClinica[] | null>(null);
    const [escolhida, setEscolhida] = useState<string | null>(() => unidadeAtiva());
    const compacta = useTopbarCompacta();

    useEffect(() => {
        if (!clinicaId) return;
        let vivo = true;
        const carregar = () => {
            /* A chamada dentro do `then`: uma porta sem o método vira recusa, e não tela quebrada. */
            Promise.resolve()
                .then(() => dados().unidadesAtivas())
                .then((lista) => {
                    if (!vivo) return;
                    setUnidades(lista);
                    const guardada = unidadeAtiva();
                    if (guardada && !lista.some((u) => u.id === guardada)) {
                        trocarUnidade(null);
                        setEscolhida(null);
                        void revalidar.current();
                    }
                })
                /* Sem a lista o seletor não aparece; a tela que depende das unidades
                   mostra o próprio erro ao ler. */
                .catch(() => { if (vivo) setUnidades([]); });
        };
        carregar();
        window.addEventListener(EVENTO_DAS_UNIDADES, carregar);
        return () => {
            vivo = false;
            window.removeEventListener(EVENTO_DAS_UNIDADES, carregar);
        };
    }, [clinicaId]);

    if (!clinicaId || !unidades || unidades.length < 2) return null;

    const opcoes = [{ value: TODAS, label: 'Todas as unidades' }, ...unidades.map((u) => ({ value: u.id, label: u.nome }))];
    const atualValor = escolhida ?? TODAS;
    const escolher = (v: string) => {
        const id = v === TODAS ? null : v;
        trocarUnidade(id);
        setEscolhida(id);
        void revalidar.current();
    };

    /* No celular o seletor de 180 px empurrava a conta para fora da tela. Vira
       ícone que abre as mesmas opções; a unidade escolhida segue no nome
       acessível e no title, e marcada na lista. */
    if (compacta) {
        const rotulo = `Unidade: ${opcoes.find((o) => o.value === atualValor)?.label ?? 'Todas as unidades'}`;
        return (
            <Menu
                align="right" width={220} onSelect={escolher}
                items={opcoes.map((o) => ({ id: o.value, label: o.label, icon: o.value === atualValor ? 'check' : undefined }))}
                trigger={(
                    <button
                        type="button" aria-label={rotulo} title={rotulo}
                        style={{
                            width: 32, height: 32, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            border: '1px solid var(--border)', borderRadius: 'var(--radius-control)', background: 'var(--surface)',
                            color: escolhida ? 'var(--text-brand)' : 'var(--neutral-600)', cursor: 'pointer',
                        }}
                    >
                        <Icon name="map-pin" size={16} />
                    </button>
                )}
            />
        );
    }

    return (
        <div style={{ width: 180, flexShrink: 1, minWidth: 120 }}>
            <Select value={atualValor} size="sm" aria-label="Unidade" onChange={escolher} options={opcoes} />
        </div>
    );
}
